import type { Unit } from './types'

/**
 * Which of a design's machines have drawn its latest publish.
 *
 * "Version 16 live" says what the server will hand out; it says nothing about
 * what is on the glass. Found on 1 October 2026: a kiosk drew a published design
 * with the app from before a fix, and the editor could not tell. Each machine now
 * reports what it drew (LiveConnection.report), and this reads those reports
 * against the version the editor is showing.
 *
 * Judged here against the open design rather than by the server's designBehind:
 * the editor has just published, and its version is the one in question.
 */
export type Behind = { unit: Unit; why: 'offline' | 'version' | 'app'; version?: number }

export function reach(layoutId: string, version: number, machines: readonly Unit[]): { showing: number; of: number; behind: Behind[] } {
  const behind: Behind[] = []
  for (const unit of machines) {
    const s = unit.showing
    if (!unit.running) behind.push({ unit, why: 'offline' })
    // Connected and silent is a page from before machines reported anything.
    else if (!s) behind.push({ unit, why: 'app' })
    else if (s.layoutId !== layoutId || s.version !== version) behind.push({ unit, why: 'version', version: s.layoutId === layoutId ? s.version : undefined })
    else if (s.appBehind) behind.push({ unit, why: 'app' })
  }
  return { showing: machines.length - behind.length, of: machines.length, behind }
}

/**
 * Whether a running machine on the Machines page is behind, by the server's own
 * judgement: on a design or version other than the one published for it, or on
 * the app from before the last deploy. A kiosk or board connected and silent is a
 * page from before machines reported anything — old app too.
 */
export function lagOf(unit: Unit): 'design' | 'app' | null {
  if (!unit.running || (unit.kind !== 'kiosk' && unit.kind !== 'display' && unit.kind !== 'desk_display')) return null
  if (!unit.showing) return 'app'
  if (unit.showing.appBehind) return 'app'
  if (unit.showing.designBehind) return 'design'
  return null
}
